<template>
  <div class="company-map ml-4 mr-4">
    <div v-if="type === 'create'">
      <div class="row py-3 align-items-center">
        <div class="col company-h1">
          <button type="button"
                  class="btn btn-back"
                  @click="setCompanyStep('companies-info')"
          />
          Create Company
        </div>
        <div class="col text-right">
          <button type="button"
                  class="btn btn-primary ld-ext-right"
                  :class="loadingButton ? 'running' : ''"
                  :disabled="buttonDisabled"
                  @click="saveAvailability()"
          >
            Save
            <div class="ld ld-ball ld-flip" />
          </button>
        </div>
      </div>
      <companies-steps-menu type="create" />
    </div>
    <div v-if="type === 'edit'">
      <div class="row py-3 align-items-center company-header">
        <div class="col-4">
          <h2 class="company-h1 mr-3">
            <button type="button"
                    class="btn btn-back"
                    @click="$router.push('/companies')"
            />
            {{ company.name }}
          </h2>
          <template v-if="company.status">
            <span v-if="company.status.name === 'Active'"
                  :class="'btn-status btn-active'"
            >{{ company.status.name }}</span>
            <span v-if="company.status.name === 'Inactive'"
                  :class="'btn-status btn-closed'"
            >{{ company.status.name }}</span>
            <span v-if="company.status.name === 'Setup Incomplete'"
                  :class="'btn-status btn-ncover info-company'"
            >{{ company.status.name }}
              <div class="btn btn-link p-0 tippy-popup tippy-info" @mouseover="showTooltipPrefix = true"
                   @mouseout="showTooltipPrefix = false"
              >
                <i class="fa fa-info-circle" />
              </div>
              <div class="popover tippy-info-popover" v-if="showTooltipPrefix">
                Setup incomplete is present when the company doesn't have one or more of the following: move sizes, arrival windows, merchant account, rates and coverage.
              </div>
            </span>
          </template>
        </div>
        <div class="col-8 d-flex align-items-center justify-content-end">
          <companies-visibility-bar
            :company="company"
            @saved="$emit('saved')"
            v-if="$_can('companies.review.request')"
          />
          <button type="button"
                  class="btn btn-primary ld-ext-right ml-5"
                  :class="loadingButton ? 'running' : ''"
                  :disabled="buttonDisabled"
                  v-if="$_can('companies.coverage.update')"
                  @click="saveAvailability()"
          >
            Save
            <div class="ld ld-ball ld-flip" />
          </button>
        </div>
      </div>
    </div>

    <div class="row mb-3">
      <div class="col-4">
        <div class="dropdown dropdown-menu-right pr-3">
          <div class="select-wrap" id="dropdownMenuButton"
               data-toggle="dropdown" aria-haspopup="true"
               aria-expanded="false"
          >
            <label>
              Automatic assignment
            </label>
            <span class="d-flex justify-content-between align-items-center">
              <input class="form-control pointer"
                     type="text"
                     v-if="company.automatic_assignment_type"
                     v-model="company.automatic_assignment_type.name"
                     readonly
              />
              <i class="fa fa-caret-down pr-3" />
            </span>
          </div>
          <div class="dropdown-menu dropdown-menu-right" aria-labelledby="dropdownMenuButton">
            <div class="dropdown-box border-bottom pointer"
                 v-for="assignmentType in assignmentTypes"
                 :key="assignmentType.id"
                 @click.prevent="selectType(assignmentType)"
            >
              <button class="dropdown-item">
                {{ assignmentType.name }}
              </button>
              <p>{{ assignmentType.description }}</p>
            </div>
          </div>
        </div>
      </div>
      <div class="col-4">
        <div class="select-wrap">
          <label>Share with</label>
          <key-multiselect v-if="carriers" v-model="company.shared_with_ids"
                           :options="carriers.filter(carrier => carrier.id !== company.default_carrier_id)"
                           :multiple="true"
                           :clear-on-select="false"
                           :close-on-select="false"
                           :hide-selected="true"
                           :preserve-search="false"
                           label="name"
                           track-by="id"
                           :limit="carriers.length"
          >
            <template slot="tag" slot-scope="props">
              <div class="multiselect__tag">
                <span>{{ props.option.name }}</span>
                <span class="multiselect__tag-icon" @click="props.remove(props.option)" />
              </div>
            </template>
          </key-multiselect>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import axios from 'axios';
import toastr from 'toastr';
import { mapActions, mapMutations, mapState } from 'vuex';
import CompaniesStepsMenu from './CompaniesStepsMenu';
import CompaniesVisibilityBar from './CompaniesVisibilityBar';

export default {
  components: {
    CompaniesStepsMenu,
    CompaniesVisibilityBar,
  },
  props: {
    type: {
      type: String,
      default: 'edit',
    },
    company: {
      type: Object,
      default: () => ({
        address2: '',
        address: '',
        cash_discount_unit: '',
        cash_discount_value: '',
        city: '',
        custom_rate: null,
        default_carrier_id: '',
        deposit_unit: '$',
        deposit_value: '',
        email: '',
        fixed_rate: 1,
        meta_description: '',
        meta_title: '',
        minimum_hours: '',
        name: '',
        offer_duration: 2,
        parking_address: '',
        parking_address2: '',
        parking_full_address: '',
        parking_city: '',
        parking_latitude: '',
        parking_longitude: '',
        parking_state: '',
        parking_state_id: '',
        parking_zip: '',
        phone: '',
        ref_id_prefix: '',
        state_id: '',
        truck_price: '',
        zip: '',
      }),
    },
  },
  data() {
    return {
      assignmentTypes: [],
      buttonDisabled: false,
      loadingButton: false,
      showTooltipPrefix: false,
    };
  },
  computed: {
    ...mapState({
      carriers: state => state.carriers.carriers.data,
    }),
  },
  mounted() {
    this.getAssignmentTypes();
  },
  methods: {
    ...mapActions(['getCarriers', 'getCompanies']),
    ...mapMutations(['setCompanyStep', 'setSavedCoverage']),
    saveAvailability() {
      this.buttonDisabled = true;
      this.loadingButton = true;
      axios
        .put(`/companies/${this.$route.params.id}`, this.company)
        .then(() => {
          if (this.type === 'edit') {
            toastr.success('Successfully saved availability');
          } else {
            this.setCompanyStep('companies-settings');
          }
          this.getCompanies();
          this.$emit('saved');
        })
        .catch(err => {
          this.errors.record(err.response.data.error);
        })
        .finally(() => {
          this.loadingButton = false;
          this.buttonDisabled = false;
        });
    },
    getAssignmentTypes() {
      axios
        .get('values/automatic-assignment-types')
        .then(res => {
          this.assignmentTypes = res.data.data;
        })
        .catch(err => this.errors.record(err.response.data.error));
    },
    selectType(type) {
      this.$set(this.company, 'automatic_assignment_type', type);
      this.$set(this.company, 'automatic_assignment_type_id', type.id);
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/companies/CompaniesAvailability.vue